import React from "react";
import { Box, Typography, Button, Chip, alpha, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import StyledTextField from "../../../components/Fields/Textfield";
import StyledSelect from "../../../components/Fields/SingleSelect";
import { displayFileName } from "../../../components/ui/icons";
import type { DocumentSlot, FormProps } from "../types";

interface RequestSpecsFormProps extends FormProps {
  comparisonName: string;
  onComparisonNameChange: (value: string) => void;
  comparisonType: string;
  onComparisonTypeChange: (value: string) => void;
  document1: DocumentSlot;
  document2: DocumentSlot;
  renderDocumentSlot: (
    document: DocumentSlot,
    slotNumber: 1 | 2,
    label: string,
  ) => React.ReactNode;
}

const RequestSpecsForm: React.FC<RequestSpecsFormProps> = ({
  comparisonName,
  onComparisonNameChange,
  comparisonType,
  onComparisonTypeChange,
  document1,
  document2,
  renderDocumentSlot,
  isProcessing,
  isSmallScreen,
  onOpenBottomDrawer,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const getDocumentLabel = (slotNumber: 1 | 2): string =>
    slotNumber === 1
      ? t("requestSpecs.bestek1")
      : t("requestSpecs.bestek2");

  // The form renders inside the navy config panel on desktop and on a light
  // surface on small screens. Mid-teal tokens (brand.icon / brand.ocean /
  // primary.main) are unreadable as text on navy — use white / brand.cyanHead
  // (6.86:1 on navy) instead (UI/UX audit 2.4.1, WCAG 1.4.3).
  const onNavy = !isSmallScreen;
  const bodyColor = onNavy
    ? alpha(theme.palette.common.white, 0.85)
    : theme.palette.text.secondary;
  const successColor = onNavy
    ? theme.palette.brand.cyanHead
    : theme.palette.status.successFg;
  const chipSx = {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    fontSize: "11px",
    color: onNavy ? theme.palette.brand.cyanHead : theme.palette.text.primary,
    borderColor: onNavy
      ? theme.palette.brand.cyanHead
      : theme.palette.primary.main,
  } as const;

  const bothDocumentsChosen = Boolean(document1 && document2);

  return (
    <>
      <StyledTextField
        placeholder={t("functionalities.form.comparisonNamePlaceholder")}
        label={t("functionalities.form.comparisonNameLabel")}
        required={true}
        value={comparisonName}
        onChange={(e) => onComparisonNameChange(e.target.value)}
        disabled={isProcessing}
      />

      <StyledSelect
        label={t("functionalities.form.comparisonTypeLabel")}
        value={comparisonType}
        onChange={(event) =>
          onComparisonTypeChange(event.target.value as string)
        }
        options={[
          {
            label: t("functionalities.form.comparisonTypeOriginal"),
            value: "Original Comparison",
          },
          {
            label: t("functionalities.form.comparisonTypeContent"),
            value: "Content Comparison",
          },
        ]}
        disabled={isProcessing}
      />
      {isSmallScreen && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Typography variant="h5">{t("common.upload")}</Typography>
          <Button
            size="large"
            variant="outlined"
            color="primary"
            onClick={onOpenBottomDrawer}
            endIcon={
              <Icon
                icon="hugeicons:arrow-up-right-01"
                width={24}
                height={24}
                color={theme.palette.primary.main}
              />
            }
          >
            {t("functionalities.form.availableSpec")}
          </Button>
        </Box>
      )}

      <Box mt={2} mb={1}>
        <Typography
          variant="h6"
          sx={{
            color: isSmallScreen
              ? theme.palette.text.primary
              : theme.palette.common.white,
            fontWeight: 600,
            fontSize: "18px",
            mb: 0.5,
          }}
        >
          {t("functionalities.form.selectDocuments")}
        </Typography>
        <Typography variant="body2" color={bodyColor} sx={{ mb: 1 }}>
          {t("requestSpecs.uploadFiles")}
        </Typography>
      </Box>

      {renderDocumentSlot(document1, 1, getDocumentLabel(1))}
      {renderDocumentSlot(document2, 2, getDocumentLabel(2))}

      {/* Selection Status */}
      {(document1 || document2) && (
        <Box
          mt={2}
          p={2}
          bgcolor={alpha(theme.palette.success.main, 0.1)}
          borderRadius={2}
          border={`1px solid ${alpha(theme.palette.success.main, 0.2)}`}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Icon
              icon="mingcute:check-circle-fill"
              style={{ fontSize: 16, color: successColor }}
            />
            <Typography
              variant="body2"
              color={successColor}
              sx={{ fontWeight: 500 }}
            >
              {t("functionalities.form.documentsSelected")}
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {document1 && (
              <Chip
                label={`${t("requestSpecs.bestek1")}: ${displayFileName(
                  "tittle" in document1 ? document1.tittle : document1.name,
                )}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={chipSx}
              />
            )}
            {document2 && (
              <Chip
                label={`${t("requestSpecs.bestek2")}: ${displayFileName(
                  "tittle" in document2 ? document2.tittle : document2.name,
                )}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={chipSx}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Instructions — only while the requirement is still unmet, so the panel
          never confirms and instructs at the same time (UI/UX audit 2.4.2). */}
      {!bothDocumentsChosen && (
        <Box
          p={2}
          bgcolor={alpha(theme.palette.info.main, 0.1)}
          borderRadius={2}
          border={`1px solid ${alpha(theme.palette.info.main, 0.2)}`}
        >
          <Typography
            variant="caption"
            color={onNavy ? theme.palette.common.white : theme.palette.info.dark}
            sx={{
              display: "block",
              fontSize: "12px",
              lineHeight: 1.4,
            }}
          >
            <Icon
              icon="mingcute:information-line"
              style={{
                fontSize: 14,
                marginRight: 4,
                verticalAlign: "text-bottom",
              }}
            />
            {comparisonType === "Content Comparison"
              ? t("functionalities.form.contentComparisonInstruction")
              : t("functionalities.form.compareInstruction")}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default RequestSpecsForm;
